import { useState } from 'react';
import { Archive, ArchiveRestore, ChevronDown, RefreshCw } from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../../../foundation/altzero_base/base/components/ui/dropdown-menu';
import { ArchiveInvoiceDialog } from '../ArchiveInvoiceDialog';
import { billService } from '../../services/billService';
import { overriddenBills, summarizeRefresh } from '../../utils/billRefreshSummary';
import type { ArchiveReason } from '../../utils/archiveReasons';
import type { BvBill } from '../../types/billTypes';

// Bill Runs' copy of the Bills list "Actions" menu: Refresh, Archive selected,
// Restore selected. Same services and confirm dialog as the Bills list; the
// difference is that Archive only offers the active bills in the selection and
// Restore only the archived ones (they show here only under "include archived").

type BillRunsBillActionsProps = {
  /** The selected bills, once each. */
  bills: BvBill[];
  /** A one-line result to show under the toolbar; null clears it. */
  onNotice: (notice: string | null) => void;
  /** Bills were changed: the page clears its selection and refetches. */
  onChanged: () => void;
};

export const BillRunsBillActions = ({ bills, onNotice, onChanged }: BillRunsBillActionsProps) => {
  const [refreshing, setRefreshing] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);

  const active = bills.filter((b) => !b.archived_at);
  const archived = bills.filter((b) => !!b.archived_at);

  // Rebuilds usage, actual total and the expected invoice; does not re-read the PDF.
  const refresh = async () => {
    // Job 52, in bulk: asked once for the whole selection, as on the Bills list.
    const edited = overriddenBills(bills);
    const replaceOverridden = edited.length > 0 && confirm(
      `${edited.length} of the selected bills have an expected invoice that was edited by hand.\n\n`
      + 'OK replaces those edits with freshly calculated values.\n'
      + 'Cancel refreshes everything else and leaves them exactly as they are.',
    );

    setRefreshing(true);
    onNotice(`Refreshing ${bills.length} bill${bills.length === 1 ? '' : 's'}…`);
    try {
      const res = await billService.refreshBills(bills.map((b) => b.id), { replaceOverridden });
      onNotice(res.success && res.data ? summarizeRefresh(res.data.outcomes) : res.error ?? 'Refresh failed');
      if (res.success) onChanged();
    } catch (err) {
      onNotice(err instanceof Error ? err.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const archive = async (reason: ArchiveReason, note: string) => {
    const count = active.length;
    setArchiveBusy(true);
    const res = await billService.archive(active.map((b) => b.id), reason, note);
    setArchiveBusy(false);
    setArchiveOpen(false);
    // Refetch either way: a partial failure still moved rows. The selection is
    // cleared with it, so the error goes under the toolbar, not in the dialog.
    onChanged();
    onNotice(res.success ? `${count} bill${count === 1 ? '' : 's'} archived.` : res.error ?? 'Archive failed');
  };

  const restore = async () => {
    const count = archived.length;
    onNotice(null);
    const res = await billService.restore(archived.map((b) => b.id));
    onChanged();
    onNotice(res.success ? `${count} bill${count === 1 ? '' : 's'} restored.` : res.error ?? 'Restore failed');
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="bill-runs2-action-menu" variant="outline" size="sm" className="gap-1.5" disabled={refreshing || bills.length === 0}>
            {refreshing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Actions
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid="bill-runs2-action-refresh" onClick={() => void refresh()}>
            <RefreshCw className="h-3.5 w-3.5 mr-2" /> Refresh
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="bill-runs2-action-archive" disabled={active.length === 0}
            onClick={() => setArchiveOpen(true)}>
            <Archive className="h-3.5 w-3.5 mr-2" /> Archive selected{archived.length > 0 && ` (${active.length})`}
          </DropdownMenuItem>
          {archived.length > 0 && (
            <DropdownMenuItem data-testid="bill-runs2-action-restore" onClick={() => void restore()}>
              <ArchiveRestore className="h-3.5 w-3.5 mr-2" /> Restore selected ({archived.length})
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ArchiveInvoiceDialog
        open={archiveOpen}
        count={active.length}
        busy={archiveBusy}
        error={null}
        onCancel={() => { if (!archiveBusy) setArchiveOpen(false); }}
        onConfirm={(reason, note) => void archive(reason, note)}
      />
    </>
  );
};
